// App root component

import { CacheProvider } from '@emotion/react'
import { loadIcons } from '@iconify/react'
import { Box, GlobalStyles } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { registerLicense } from '@syncfusion/ej2-base'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useEffect } from 'react'
import { Toaster } from 'react-hot-toast'
import { Provider } from 'react-redux'
import { RouterProvider } from 'react-router-dom'
import { setLocale } from 'yup'

import 'src/configs/i18n'
import 'src/@fake-db'
import 'src/@local-db'
import 'src/iconify-bundle/icons-bundle-react'
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import 'prismjs/components/prism-jsx'
import 'prismjs/components/prism-tsx'
import 'react-perfect-scrollbar/dist/css/styles.css'

import { SettingsConsumer, SettingsProvider } from 'src/@core/context/settingsContext'
import ReactHotToast from 'src/@core/styles/libs/react-hot-toast'
import ThemeComponent from 'src/@core/theme/ThemeComponent'
import { createEmotionCache } from 'src/@core/utils/create-emotion-cache'
import MqttConnector from 'src/client/mqtt/Connector'
import { store } from 'src/store'
import { titleCase } from 'src/utils/general'
import { Toast } from '@/components/atoms/toast'
import { GlobalErrorBoundary, reportGlobalError } from '@/components/templates/error-boundary/GlobalErrorBoundary'
import { useAlert } from '@/hooks/stores/useAlert'
import { env } from '@/utils/env'
import { queryClient } from '@/utils/queryClient'
import { LocalDbDevtools } from './@local-db/components/LocalDbDevtools'
import { router } from './routes'

const clientSideEmotionCache = createEmotionCache()

setLocale({
  mixed: {
    required(params) {
      const message = 'is a required field'
      if (params.label) return `${params.label} ${message}`
      const splittedLabel = !params.path.match(/[\s_-]/g) ? params.path.split(/(?=[A-Z])/) : params.path.split('_')
      return `${titleCase(splittedLabel.join(' '))} ${message}`
    }
  }
})

const syncfusionKey = env('NEXT_PUBLIC_SYNCFUSION_LICENSE_KEY')
if (syncfusionKey) {
  registerLicense(syncfusionKey)
}

loadIcons(['ic:baseline-check', 'ic:baseline-do-disturb'])

export default function App() {
  const { alert, setAlert } = useAlert()

  useEffect(() => {
    const isApiError = (reason: unknown): boolean => {
      if (!reason || typeof reason !== 'object') return false
      if ('isAxiosError' in reason && (reason as any).isAxiosError === true) return true
      if ('response' in reason && 'status' in (reason as any).response) return true
      if ('statusCode' in reason && typeof (reason as any).statusCode === 'number') return true
      if ('status' in reason && typeof (reason as any).status === 'number') {
        const status = (reason as any).status
        if (status >= 100 && status < 600) return true
      }
      return false
    }

    const onError = (event: ErrorEvent) => {
      if (!event.error) return
      const error = event.error instanceof Error ? event.error : new Error(String(event.error))
      console.error('Global runtime error', error)
      reportGlobalError(error)
    }

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason
      if (isApiError(reason)) {
        console.warn('Unhandled API rejection:', reason)
        return
      }
      if (reason === undefined || reason === null) return
      const error = reason instanceof Error ? reason : new Error(String(reason ?? 'Unhandled promise rejection'))
      console.error('Unhandled promise rejection', error)
      reportGlobalError(error)
    }

    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onUnhandledRejection)

    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onUnhandledRejection)
    }
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Provider store={store}>
          <MqttConnector>
            <CacheProvider value={clientSideEmotionCache}>
              <GlobalStyles
                styles={{
                  html: {
                    fontFamily: "Inter, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif"
                  }
                }}
              />
              <SettingsProvider>
                <SettingsConsumer>
                  {({ settings }) => (
                    <ThemeComponent settings={settings}>
                      <GlobalErrorBoundary>
                        <RouterProvider router={router} />
                      </GlobalErrorBoundary>
                      <LocalDbDevtools />
                      <ReactHotToast>
                        <Toaster position={settings.toastPosition} toastOptions={{ className: 'react-hot-toast' }} />
                      </ReactHotToast>
                      {alert.open && (
                        <Box
                          sx={{
                            position: 'absolute',
                            zIndex: 9999,
                            top: 80,
                            right: '2%'
                          }}
                        >
                          <Toast
                            type='alert'
                            icon={alert.variant === 'success' ? 'material-symbols:check' : 'tabler:alert-triangle'}
                            subTitle={alert.subTitle as string}
                            variant={alert.variant}
                            content={alert.content}
                            title={alert.title as string}
                            onClose={() => setAlert({ ...alert, open: false })}
                          />
                        </Box>
                      )}
                    </ThemeComponent>
                  )}
                </SettingsConsumer>
              </SettingsProvider>
            </CacheProvider>
          </MqttConnector>
        </Provider>
      </LocalizationProvider>
      <ReactQueryDevtools />
    </QueryClientProvider>
  )
}
